import type { SampledTrack } from '@rcs/track-geometry';

/** コースメッシュの寸法 [m]。 */
export interface TrackMeshOptions {
  /** 2 本のレールの中心間隔（既定 1.2） */
  readonly gauge?: number;
  /** レールの断面（正方形）の一辺（既定 0.12） */
  readonly railSize?: number;
  /** レールの頂点列を取るおおよその間隔（既定 0.5）。細かいほど滑らかで重い */
  readonly railSpacing?: number;
  /** 枕木の間隔（既定 1.0） */
  readonly tieSpacing?: number;
  /** 枕木の奥行き（コース方向の長さ、既定 0.18） */
  readonly tieDepth?: number;
  /** 枕木の厚み（既定 0.06） */
  readonly tieThickness?: number;
  /** 枕木がレールの外側へはみ出す長さ（片側、既定 0.2） */
  readonly tieOverhang?: number;
}

/** 既定の寸法。 */
export const DEFAULT_TRACK_MESH_OPTIONS: Required<TrackMeshOptions> = {
  gauge: 1.2,
  railSize: 0.12,
  railSpacing: 0.5,
  tieSpacing: 1.0,
  tieDepth: 0.18,
  tieThickness: 0.06,
  tieOverhang: 0.2,
};

/** レール 1 本分の頂点データ。three に依存しない素の配列。 */
export interface RailGeometryData {
  /** 頂点の色（長さ `頂点数 * 3`、0〜1）。`segmentColors` を指定したときだけ作る */
  readonly colors?: Float32Array;
  /** 頂点位置（長さ `頂点数 * 3`） */
  readonly positions: Float32Array;
  /** 頂点法線（面ごとの平坦な法線） */
  readonly normals: Float32Array;
  /** 三角形の頂点番号（外向きが表） */
  readonly indices: Uint32Array;
}

/** 断面の頂点数（4 面 × 面ごとに 2 頂点）。 */
const RING_VERTICES = 8;

function resolve(options: TrackMeshOptions): Required<TrackMeshOptions> {
  return { ...DEFAULT_TRACK_MESH_OPTIONS, ...options };
}

/** 右方ベクトル R = T × U。 */
function rightAt(track: SampledTrack, k: number): [number, number, number] {
  const tx = track.tangent[k * 3] ?? 0;
  const ty = track.tangent[k * 3 + 1] ?? 0;
  const tz = track.tangent[k * 3 + 2] ?? 0;
  const ux = track.up[k * 3] ?? 0;
  const uy = track.up[k * 3 + 1] ?? 0;
  const uz = track.up[k * 3 + 2] ?? 0;
  return [ty * uz - tz * uy, tz * ux - tx * uz, tx * uy - ty * ux];
}

/** レールの断面を作るサンプル番号。間引き、最後のサンプルを含め、周回コースは始点へ戻って閉じる。 */
function ringSamples(track: SampledTrack, railSpacing: number): number[] {
  const stride = Math.max(1, Math.round(railSpacing / track.spacing));
  const samples: number[] = [];
  for (let k = 0; k < track.count; k += stride) samples.push(k);
  const last = track.count - 1;
  if (samples[samples.length - 1] !== last) samples.push(last);
  if (track.closed) {
    const dx = (track.position[last * 3] ?? 0) - (track.position[0] ?? 0);
    const dy = (track.position[last * 3 + 1] ?? 0) - (track.position[1] ?? 0);
    const dz = (track.position[last * 3 + 2] ?? 0) - (track.position[2] ?? 0);
    if (Math.hypot(dx, dy, dz) > 1e-6) samples.push(0);
  }
  return samples;
}

/**
 * レール 1 本の頂点データを作る。断面は一辺 `railSize` の正方形で、
 * 中心線から右方向（R）へ `side * gauge / 2` ずらした位置を、サンプル軌道に沿って掃引する。
 *
 * @param side `-1` で左のレール、`1` で右のレール
 * @param tint 区間ごとのレールの色（走行中も、リフト・ブレーキなどの区間を見分けられるように）。
 *   `segmentColors[区間番号]` が未指定の区間は `defaultColor`（`0xrrggbb`）
 */
export function buildRailGeometry(
  track: SampledTrack,
  side: -1 | 1,
  options: TrackMeshOptions = {},
  tint?: {
    readonly segmentColors: readonly (number | undefined)[];
    readonly defaultColor: number;
  },
): RailGeometryData {
  const o = resolve(options);
  const samples = ringSamples(track, o.railSpacing);
  const rings = samples.length;
  const positions = new Float32Array(rings * RING_VERTICES * 3);
  const normals = new Float32Array(rings * RING_VERTICES * 3);
  const colors = tint === undefined ? undefined : new Float32Array(rings * RING_VERTICES * 3);
  const half = o.railSize / 2;
  // 断面の四隅（R 方向, U 方向）。反時計回りに c0 → c1 → c2 → c3
  const corners: ReadonlyArray<readonly [number, number]> = [
    [half, half],
    [-half, half],
    [-half, -half],
    [half, -half],
  ];
  // 各面の外向き法線（R 方向, U 方向）。面 f は corners[f] と corners[(f + 1) % 4] の間
  const faceNormals: ReadonlyArray<readonly [number, number]> = [
    [0, 1],
    [-1, 0],
    [0, -1],
    [1, 0],
  ];

  samples.forEach((k, ring) => {
    const [rx, ry, rz] = rightAt(track, k);
    const ux = track.up[k * 3] ?? 0;
    const uy = track.up[k * 3 + 1] ?? 0;
    const uz = track.up[k * 3 + 2] ?? 0;
    const offset = (side * o.gauge) / 2;
    const hex = tint?.segmentColors[track.segment[k] ?? 0] ?? tint?.defaultColor ?? 0;
    const cx = (track.position[k * 3] ?? 0) + rx * offset;
    const cy = (track.position[k * 3 + 1] ?? 0) + ry * offset;
    const cz = (track.position[k * 3 + 2] ?? 0) + rz * offset;
    for (let f = 0; f < 4; f += 1) {
      const [nr, nu] = faceNormals[f] ?? [0, 0];
      for (let j = 0; j < 2; j += 1) {
        const [cr, cu] = corners[(f + j) % 4] ?? [0, 0];
        const v = (ring * RING_VERTICES + f * 2 + j) * 3;
        positions[v] = cx + rx * cr + ux * cu;
        positions[v + 1] = cy + ry * cr + uy * cu;
        positions[v + 2] = cz + rz * cr + uz * cu;
        if (colors !== undefined) {
          colors[v] = ((hex >> 16) & 0xff) / 255;
          colors[v + 1] = ((hex >> 8) & 0xff) / 255;
          colors[v + 2] = (hex & 0xff) / 255;
        }
        normals[v] = rx * nr + ux * nu;
        normals[v + 1] = ry * nr + uy * nu;
        normals[v + 2] = rz * nr + uz * nu;
      }
    }
  });

  const indices = new Uint32Array((rings - 1) * 4 * 6);
  let n = 0;
  for (let ring = 0; ring < rings - 1; ring += 1) {
    for (let f = 0; f < 4; f += 1) {
      const a = ring * RING_VERTICES + f * 2;
      const b = a + 1;
      const c = a + RING_VERTICES;
      const d = c + 1;
      indices[n++] = a;
      indices[n++] = c;
      indices[n++] = b;
      indices[n++] = b;
      indices[n++] = c;
      indices[n++] = d;
    }
  }
  return colors === undefined
    ? { positions, normals, indices }
    : { positions, normals, indices, colors };
}

/** 枕木の本数。 */
export function tieCount(track: SampledTrack, options: TrackMeshOptions = {}): number {
  const o = resolve(options);
  return Math.max(1, Math.floor(track.length / o.tieSpacing) + 1);
}

/**
 * 枕木ごとの区間番号（`track.segment`）。区間ごとに枕木の色を変える（リフト・ブレーキなどの表示）ために使う。
 * 長さは {@link tieCount} と同じで、`buildTieMatrices` の並びと対応する。
 */
export function tieSegments(track: SampledTrack, options: TrackMeshOptions = {}): Uint32Array {
  const o = resolve(options);
  const count = tieCount(track, o);
  const segments = new Uint32Array(count);
  for (let i = 0; i < count; i += 1) {
    const s = Math.min(i * o.tieSpacing, track.length);
    const k = Math.min(track.count - 1, Math.max(0, Math.round(s / track.spacing)));
    segments[i] = track.segment[k] ?? 0;
  }
  return segments;
}

/**
 * 枕木の配置行列（4×4、列優先、1 本 16 要素）を作る。
 * 単位の箱（1×1×1）を、レールの下に、コースに直交して置く行列で、`InstancedMesh` にそのまま渡せる。
 * 箱の局所 X = 右（R）、Y = 上（U）、Z = 後方（-T）。
 */
export function buildTieMatrices(
  track: SampledTrack,
  options: TrackMeshOptions = {},
): Float32Array {
  const o = resolve(options);
  const count = tieCount(track, o);
  const matrices = new Float32Array(count * 16);
  const width = o.gauge + o.railSize + 2 * o.tieOverhang;
  for (let i = 0; i < count; i += 1) {
    const s = Math.min(i * o.tieSpacing, track.length);
    const k = Math.min(track.count - 1, Math.max(0, Math.round(s / track.spacing)));
    const [rx, ry, rz] = rightAt(track, k);
    const tx = track.tangent[k * 3] ?? 0;
    const ty = track.tangent[k * 3 + 1] ?? 0;
    const tz = track.tangent[k * 3 + 2] ?? 0;
    const ux = track.up[k * 3] ?? 0;
    const uy = track.up[k * 3 + 1] ?? 0;
    const uz = track.up[k * 3 + 2] ?? 0;
    // レールの断面の下端に、枕木の上面が接する
    const drop = o.railSize / 2 + o.tieThickness / 2;
    const px = (track.position[k * 3] ?? 0) - ux * drop;
    const py = (track.position[k * 3 + 1] ?? 0) - uy * drop;
    const pz = (track.position[k * 3 + 2] ?? 0) - uz * drop;
    const m = i * 16;
    // 列 0 = X 軸（幅）、列 1 = Y 軸（厚み）、列 2 = Z 軸（奥行き、-T）、列 3 = 位置
    matrices.set(
      [
        rx * width,
        ry * width,
        rz * width,
        0,
        ux * o.tieThickness,
        uy * o.tieThickness,
        uz * o.tieThickness,
        0,
        -tx * o.tieDepth,
        -ty * o.tieDepth,
        -tz * o.tieDepth,
        0,
        px,
        py,
        pz,
        1,
      ],
      m,
    );
  }
  return matrices;
}

/** コースに付ける設備の種類（走行中の映像で、区間の違いが分かるようにする）。 */
export type DecorationKind = 'lift' | 'launch' | 'brake' | 'station';

/** 設備 1 つ分の配置：軌道に沿った間隔ごとに、右方向・上方向へずらして、箱を置く。 */
interface DecorationItem {
  readonly offsetRight: number;
  readonly offsetUp: number;
  /** 箱の大きさ（右・上・奥行き） */
  readonly size: readonly [number, number, number];
}

/** 設備の見た目：色・並べる間隔・箱の配置。 */
export interface DecorationStyle {
  readonly color: number;
  /** 並べる間隔 [m] */
  readonly spacing: number;
  readonly items: readonly DecorationItem[];
}

/**
 * 設備の見た目（レールの寸法に合わせる）。
 *
 * - リフト：レールの間のチェーン（細かい駒を並べる）
 * - 発進加速：レールの間の推進装置（コイルの列）
 * - ブレーキ：レールの内側の左右に、ブレーキのフィン
 * - 駅：コースの両側のホーム
 */
export function decorationStyle(
  kind: DecorationKind,
  options: TrackMeshOptions = {},
): DecorationStyle {
  const o = resolve(options);
  switch (kind) {
    case 'lift':
      return {
        color: 0x374151,
        spacing: 0.3,
        items: [{ offsetRight: 0, offsetUp: o.railSize * 0.1, size: [0.12, 0.07, 0.2] }],
      };
    case 'launch':
      return {
        color: 0x0891b2,
        spacing: 0.7,
        items: [{ offsetRight: 0, offsetUp: -o.railSize * 0.2, size: [0.45, 0.08, 0.5] }],
      };
    case 'brake':
      return {
        color: 0xdc2626,
        spacing: 1.2,
        items: [-1, 1].map((side) => ({
          offsetRight: side * (o.gauge / 2 - 0.2),
          offsetUp: o.railSize / 2 + 0.2,
          size: [0.08, 0.4, 0.8] as const,
        })),
      };
    case 'station':
      return {
        color: 0x16a34a,
        spacing: 1,
        items: [-1, 1].map((side) => ({
          offsetRight: side * (o.gauge / 2 + 1.4),
          offsetUp: -0.5,
          size: [1.6, 0.3, 1.02] as const,
        })),
      };
  }
}

/** 設備の配置行列（種類ごと）。 */
export interface DecorationData {
  readonly kind: DecorationKind;
  /** 4×4 の配置行列（列優先、1 個 16 要素）。`InstancedMesh` にそのまま渡せる */
  readonly matrices: Float32Array;
  readonly count: number;
}

/**
 * 区間ごとの設備の配置行列を作る。`decorations[区間番号]` が設備の種類（なければ設備なし）。
 * 軌道に沿って、種類ごとの間隔で、その位置の区間の設備を置く。
 */
export function buildDecorationMatrices(
  track: SampledTrack,
  decorations: readonly (DecorationKind | undefined)[],
  options: TrackMeshOptions = {},
): DecorationData[] {
  const kinds = [
    ...new Set(decorations.filter((kind): kind is DecorationKind => kind !== undefined)),
  ];
  return kinds.flatMap((kind) => {
    const style = decorationStyle(kind, options);
    const values: number[] = [];
    for (let s = 0; s <= track.length; s += style.spacing) {
      const k = Math.min(track.count - 1, Math.max(0, Math.round(s / track.spacing)));
      if (decorations[track.segment[k] ?? 0] !== kind) continue;
      const [rx, ry, rz] = rightAt(track, k);
      const tx = track.tangent[k * 3] ?? 0;
      const ty = track.tangent[k * 3 + 1] ?? 0;
      const tz = track.tangent[k * 3 + 2] ?? 0;
      const ux = track.up[k * 3] ?? 0;
      const uy = track.up[k * 3 + 1] ?? 0;
      const uz = track.up[k * 3 + 2] ?? 0;
      for (const item of style.items) {
        const [w, h, d] = item.size;
        values.push(
          rx * w,
          ry * w,
          rz * w,
          0,
          ux * h,
          uy * h,
          uz * h,
          0,
          -tx * d,
          -ty * d,
          -tz * d,
          0,
          (track.position[k * 3] ?? 0) + rx * item.offsetRight + ux * item.offsetUp,
          (track.position[k * 3 + 1] ?? 0) + ry * item.offsetRight + uy * item.offsetUp,
          (track.position[k * 3 + 2] ?? 0) + rz * item.offsetRight + uz * item.offsetUp,
          1,
        );
      }
    }
    return values.length === 0
      ? []
      : [{ kind, matrices: new Float32Array(values), count: values.length / 16 }];
  });
}
